import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { axiosClient2, patch } from "../axios";
import { orderStatusses } from "../utils/constant";
import { OrderStatus } from "../utils/enum";
import { errorToast } from "../utils/helpers";
import { AppsmenError, Order } from "../utils/interfaces";
import useOrdersQueryKey from "../v2/hooks/useOrdersQueryKey";
import useBranchId from "./useBranchId";

interface UpdateTimeProps {
  orderId: number;
  time: string;
  date: string;
  branchId: number;
}
const useLiveOrdersHook = () => {
  const queryClient = useQueryClient();
  const { queryKey } = useOrdersQueryKey();
  const branch = useBranchId();

  const { t } = useTranslation();

  const updateOrderTime = useMutation<
    unknown,
    AppsmenError,
    UpdateTimeProps,
    any
  >({
    mutationFn: (updateData) => {
      return patch(
        `internal/secure/pos/v1/orders/information/time/${updateData.branchId}/${updateData.orderId}`,
        {
          time: updateData.time,
          date: updateData.date,
        },
      );
    },
    onSuccess: () => {
      toast.success(t("success.success"));
    },
    onError: (error) => {
      if (error?.response?.status !== 406) {
        errorToast();
      }
    },
  });

  const updateStatusToConfirmed = useMutation({
    mutationFn: (data: { orderId: number; branchId: number }) => {
      return patch(
        `internal/secure/pos/v1/orders/information/status/${data.branchId}/${data.orderId}/confirmed`,
        {},
      );
    },
  });

  const update = useMutation({
    mutationFn: (updateData: {
      orderId: number;
      status: string;
      branch: number;
    }) => {
      return patch(
        `internal/secure/pos/v1/orders/information/status/${updateData.branch}/${updateData.orderId}/${updateData.status}`,
        {},
      );
    },
    onMutate: (updateData: {
      orderId: number;
      status: string;
      branch: number;
    }) => {
      const updatedStatue = orderStatusses.find(
        (statue) => statue.id === updateData.status,
      );
      const prevOrderData = queryClient.getQueryData(queryKey);
      if (!updatedStatue) {
        return { prevOrderData };
      }

      let updatedId = updatedStatue.value[1] || updatedStatue.value[0];

      updatedId =
        updatedId === OrderStatus.HANDED_OVER
          ? OrderStatus.EN_ROUTE
          : updatedId;

      const clonedPrevOrderData = JSON.parse(JSON.stringify(prevOrderData));
      if (!Array.isArray(clonedPrevOrderData)) {
        Object.keys(clonedPrevOrderData).forEach((key) => {
          const dayOrders = clonedPrevOrderData[key];
          const retIndex = dayOrders.findIndex(
            (i) => i.order_information.item_id == updateData.orderId,
          );

          if (retIndex !== -1) {
            dayOrders[retIndex].order_information.order_status = updatedId;
          }
        });
        queryClient.setQueryData(queryKey, clonedPrevOrderData);
      } else {
        clonedPrevOrderData.forEach((dayOrders: Order[]) => {
          const retIndex = dayOrders.findIndex(
            (i) => i.order_information.item_id == updateData.orderId,
          );
          if (retIndex !== -1) {
            dayOrders[retIndex].order_information.order_status = updatedId;
          }
        });
        queryClient.setQueryData(queryKey, clonedPrevOrderData);
      }

      return { prevOrderData, clonedPrevOrderData };
    },
    onSuccess: (data, _, context) => {
      queryClient.setQueryData(queryKey, context.clonedPrevOrderData);
    },
    onError: (err, newTodo, context) => {
      queryClient.setQueryData(queryKey, context.prevOrderData);
    },
    onSettled: () => {
      // queryClient.invalidateQueries({
      //   queryKey,
      // });
    },
  });

  const create = useMutation({
    mutationFn: () =>
      axiosClient2.post(`internal/orders/information/random/${branch}`),
    onSuccess: () => {
      toast.success("Willekeurige volgorde is gemaakt.t", {
        position: "bottom-right",
        toastId: "success-random-create",
      });
      queryClient.refetchQueries({ queryKey });
    },
  });

  return {
    create,
    update,
    updateOrderTime,
    updateStatusToConfirmed,
  };
};

export default useLiveOrdersHook;
